<template>
  <DataTable
    :key="tableRenderKey"
    v-model:selectedRows="selectedRows"
    data-key="operatingcostinvoicelines"
    :data="internalCostInvoiceLines"
    :columns="columns"
    :reorderableRows="false"
    :showRowActions="editMode"
    :showSplitView="false"
    :tableActions="!props.checkboxMultiselect"
    :contextMenuItemsBuilder="buildLineItemsContextMenuItems"
    :onRowClick="onRowClick"
    :onRowDblClick="() => {}"
    :onDelete="handleDelete"
    :rowClass="getInvoiceLineRowClass"
    :prefilterTabs="createOperationCostInvoiceItemsPrefilterTabs(internalCostInvoiceLines)"
    :checkboxMultiselect="props.checkboxMultiselect"
  >
    <!-- 🎯 Custom Row Actions (per-row: Delete for actual, Add for suggestions) -->
    <template #custom-rowActions="{ row }">
      <div v-if="editMode" class="flex items-center gap-1">
        <!-- DEBUG -->
        <span class="text-xs text-gray-400" style="margin-right: 8px">
          [{{ findLineItemForRow(row)?.invoiceItemSource ?? 'undefined' }}]
        </span>
        <!-- Show DELETE button only for actual items -->
        <Button
          v-if="isActualRow(row)"
          icon="pi pi-trash"
          severity="danger"
          text
          @click="() => handleDeleteSingle(row.id?.meta?.('rawvalue') ?? row.id)"
        />
        <!-- Show ADD button only for suggestions -->
        <Button v-else icon="pi pi-plus" text @click="() => handleCreateFromRow(row)" />
      </div>
    </template>
    <template #custom-primaryStart>
      <!-- 🎯 Template Source Selector Slot -->
      <slot name="templateSourceSelector">
        <OperatingCostInvoiceImportSelect
          v-if="editMode && !isNew && props.showImportSelect"
          v-model="selectedImportInvoice"
          :importSourceInvoices="props.importSourceInvoices ?? []"
          :currentContractId="props.currentContractId ?? null"
          @change="onImportFromSelected"
        />
      </slot>

      <!-- 🎯 TEST: Matching Mode Selector (for development/testing) -->
      <!-- <div class="flex items-center gap-2 ml-4 p-2 bg-yellow-50 border border-yellow-200 rounded">
        <label class="text-xs font-semibold text-yellow-800">Matching Mode (TEST):</label>
        <select
          v-model="matchingMode"
          class="px-2 py-1 text-xs border border-yellow-300 rounded bg-white"
        >
          <option value="nameOnStatement">Name on Statement</option>
          <option value="sfmPositionName">SFM Position Name</option>
          <option value="none">None (No Filtering)</option>
        </select>
        <span class="text-xs text-yellow-700">{{ matchingMode }}</span>
      </div> -->
    </template>

    <template #custom-primaryActions>
      <div class="flex items-center gap-2">
        <ButtonGroup>
          <Button
            v-if="showImportButton"
            v-tooltip="{ value: t('pages.operatingcostinvoices.importFromLatestLines') }"
            type="button"
            icon="pi pi-download"
            outlined
            @click="importFromLatest"
          />
          <Button
            v-if="editMode"
            icon="pi pi-plus"
            :label="t('labels.add')"
            @click="handleCreate"
          />
        </ButtonGroup>
      </div>
    </template>

    <template #body-nameOnStatement="{ row }">
      <div class="flex items-center gap-2">
        <i
          v-if="isDuplicateActualRow(row)"
          v-tooltip="{ value: t('titletags.duplicateCostInvoiceLineItem'), showDelay: 250 }"
          class="pi pi-exclamation-triangle duplicate-warning-icon"
          aria-hidden="true"
        />
        <span>{{ getRenderedCellValue(row, 'nameOnStatement') }}</span>
      </div>
    </template>

    <template #body-sfmPositionName="{ row }">
      <div class="flex items-center gap-2">
        <i
          v-if="isDuplicateActualRow(row)"
          v-tooltip="{ value: t('titletags.duplicateCostInvoiceLineItem'), showDelay: 250 }"
          class="pi pi-exclamation-triangle duplicate-warning-icon"
          aria-hidden="true"
        />
        <span>{{ getRenderedCellValue(row, 'sfmPositionName') }}</span>
      </div>
    </template>

    <!-- secondary toolbar actions (Delete + Apply selected) -->
    <template #custom-secondaryActions="{ confirmDeleteSelected }">
      <!-- Delete selected (wie Default) -->
      <Button
        v-if="editMode && !!actualSelectedCount"
        v-tooltip="{ value: t('labels.deleteMany', { count: actualSelectedCount }) }"
        class="ml-4"
        icon="pi pi-trash"
        severity="danger"
        :label="t('labels.deleteMany', { count: actualSelectedCount })"
        :disabled="!profileStore.userProfile?.isSeyboldUser"
        @click="onDeleteActualSelected(confirmDeleteSelected)"
      />

      <!-- Apply selected previous-year lines to current invoice -->
      <Button
        v-if="editMode && !!previousSelectedCount"
        v-tooltip="{
          value: t('pages.operatingcostinvoices.applySelectedLines', {
            count: previousSelectedCount,
          }),
        }"
        class="ml-2"
        icon="pi pi-check"
        :disabled="!profileStore.userProfile?.isSeyboldUser"
        @click="onApplyPreviousSelected"
      />
    </template>
  </DataTable>

  <Dialog v-model:visible="open" modal :header="headerTitle" :style="{ width: '50vw' }">
    <OperatingCostInvoiceLineForm
      v-if="open"
      :uuid="props.invoiceId"
      :editMode="!props.readOnlyModal"
      :isNew="isNew"
      :isCreate="isCreate"
      :formData="selectedRow"
      :initialData="lineInitialData ?? undefined"
      :placeholders="linePlaceholders"
      :suggestions="lineSuggestions"
      :suggestionSources="lineSuggestionsSources"
      :templateSource="props.templateSource"
      :disabledFields="lineDisabledFields"
      :onAddCostInvoiceLineToCreate="onAddCostInvoiceLineToCreate"
      :onAddCostInvoiceLineToUpdate="onAddCostInvoiceLineToUpdate"
      :onModalClose="onModalClose"
      :currency="effectiveCurrency"
      :daysInBillingPeriod="props.daysInBillingPeriod"
    />
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { v4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import type { LineRowClickEvent } from '@/components/datatable/dataTableTypes';
import { useDetailNavigation } from '@/components/details/hooks/useDetailNavigation';
import {
  columns,
  createOperationCostInvoiceItemsPrefilterTabs,
} from '@/views/operatingcostinvoices/components/operationcostinvoicelines/configs/operatingCostInvoiceLineColumnConfig';
import { useOperatingCostInvoices } from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { useProfileStore } from '@/stores/profile.ts';
import { isPlainRowClick } from '@/utils/dataTable';
import type { CreateOperatingCostInvoiceLineItemV1Dto } from '@/openapi';

import {
  useOperatingCostInvoiceLines,
  useOperatingCostInvoiceLinesConverter,
} from './hooks/useOperatingCostInvoiceLines';
import OperatingCostInvoiceImportSelect from './OperatingCostInvoiceImportSelect.vue';
import OperatingCostInvoiceLineForm from './OperatingCostInvoiceLineForm.vue';
import type {
  InvoiceItemSource,
  InvoiceItemSuggestionSource,
  OperatingCostInvoiceLineItemSimple,
  OperatingCostInvoiceLineItemWithSource,
} from './operatingCostInvoiceTypes';
import { isSuggestion } from './operatingCostInvoiceTypes';

const props = withDefaults(
  defineProps<{
    isNew: boolean;
    editMode: boolean;
    existingOperatingCostInvoiceLines: OperatingCostInvoiceLineItemSimple[];
    currency?: string;
    onUpdateLineItemsOnCreate: (data: OperatingCostInvoiceLineItemSimple[]) => void;
    fetchData: () => Promise<void>;
    invoiceId?: string;
    latestInvoiceId?: string;
    latestInvoiceLineItems?: OperatingCostInvoiceLineItemSimple[];
    importSourceInvoices?: OperatingCostInvoiceSimple[];
    invoiceSourceMap?: Record<string, any>; // 🎯 Map Invoice ID → Source Type (ai | latest | other)
    currentContractId?: string | null;
    selectedImportSourceInvoiceId?: string; // ID of selected invoice from importSourceInvoices
    showImportSelect?: boolean; // whether to show the import select at all (requires additional checks in parent)
    templateSource?: 'ai' | 'latest'; // 🎯 Source type of the currently selected template
    forceInvoiceItemSource?: InvoiceItemSource; // Optional display override for source icons per row
    checkboxMultiselect?: boolean; // Optional override for DataTable checkbox selection column
    disableRowModal?: boolean; // If true, clicking rows does not open modal
    readOnlyModal?: boolean; // If true, modal opens in view mode
    showSuggestionRowStyle?: boolean; // Toggle italic/gray suggestion row styling
    daysInBillingPeriod?: number;
  }>(),
  {
    currency: 'EUR',
    invoiceId: undefined,
    latestInvoiceId: undefined,
    latestInvoiceLineItems: () => [],
    importSourceInvoices: () => [],
    invoiceSourceMap: () => ({}),
    currentContractId: null,
    selectedImportSourceInvoiceId: undefined,
    showImportSelect: undefined,
    templateSource: undefined,
    forceInvoiceItemSource: undefined,
    checkboxMultiselect: undefined,
    disableRowModal: false,
    readOnlyModal: false,
    showSuggestionRowStyle: true,
    daysInBillingPeriod: undefined,
  },
);

const emit = defineEmits<{
  (e: 'lineItemCreated', payload: { ok: boolean }): void;
}>();

const { isNew, editMode } = toRefs(props);
const { t } = useI18n();
const profileStore = useProfileStore();

// Ensure currency always has a value (fallback to EUR if not provided)
const effectiveCurrency = computed(() => props.currency || 'EUR');

const { deleteOperatingCostInvoiceLines, createOperatingCostInvoiceLine } =
  useOperatingCostInvoiceLines();

const {
  convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto,
  convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList,
} = useOperatingCostInvoiceLinesConverter();

// useDetailNavigation: if invoiceId is not set, fall back to route
const { subComponentID } = useDetailNavigation({
  uuidFromProp: computed(() => props.invoiceId), // override id
});

const { fetchOperatingCostInvoice } = useOperatingCostInvoices();

const open = ref(false);
const isCreate = ref(false);
const selectedRow = ref<OperatingCostInvoiceLineItemSimple>();
const internalCostInvoiceLines = ref<OperatingCostInvoiceLineItemWithSource[]>([]);
const tableRenderKey = ref(0); // 🎯 Force DataTable re-render on modal close

const selectedRows = ref<any[]>([]);
const selectedImportInvoice = ref<OperatingCostInvoiceSimple | null>(null);

const lineInitialData = ref<Partial<OperatingCostInvoiceLineItemSimple> | null>(null);
const lineDisabledFields = ref<Partial<Record<keyof OperatingCostInvoiceLineItemSimple, boolean>>>(
  {},
);

// 🎯 Mock Placeholders for testing (will show in modal)
const linePlaceholders = ref<Partial<Record<keyof OperatingCostInvoiceLineItemSimple, string>>>({
  nameOnStatement: 'Heizkosten (Vorschlag)',
  customerArea: '245.5',
  customerAmount: '1500.00',
  totalArea: '2850.3',
  totalAmount: '18500.75',
  isFlatRate: 'false',
  status: 'SUBMITTED',
});

const lineSuggestions = ref<
  Partial<Record<keyof OperatingCostInvoiceLineItemSimple, string[] | Record<string, any>[]>>
>({});

// 🎯 NEW: Track the SOURCE of each suggestion (ai | latest | other)
// Parallel structure to lineSuggestions where each suggestion has a corresponding source
const lineSuggestionsSources = ref<
  Partial<Record<keyof OperatingCostInvoiceLineItemSimple, InvoiceItemSuggestionSource[]>>
>({});

// 🎯 NEW: Map to track source type of each line item (by line item ID)
// Used to pass source information to LineForm modal
const lineItemSourceTypeMap = ref<Map<string, InvoiceItemSuggestionSource>>(new Map());

// 🎯 Store the last loaded import source (for re-merging after delete)
const lastLoadedImportSource = ref<{
  sourceId: string;
  sourceType: InvoiceItemSuggestionSource;
  lineItems: OperatingCostInvoiceLineItemSimple[];
} | null>(null);

// 🎯 Store the ORIGINAL import source lineItems (before any modifications)
// Used for rebuilding suggestions after delete
const lastLoadedImportSourceOriginal = ref<{
  sourceId: string;
  sourceType: InvoiceItemSuggestionSource;
  lineItems: OperatingCostInvoiceLineItemSimple[];
} | null>(null);

function normalizeMatchValue(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function resolveAreaDesignationPlaceholder(value: string | undefined): string {
  const trimmed = String(value ?? '').trim();
  if (trimmed) {
    return trimmed;
  }

  // Inject fallback while assembling placeholders that are passed via props to the form.
  return props.templateSource === 'ai'
    ? t('placeholder.costInvoiceLineAreaDesignationTotalArea')
    : '';
}

const actualSelectedCount = computed(
  () => selectedRows.value.filter((row) => isActualRow(row)).length,
);

const previousSelectedCount = computed(
  () => selectedRows.value.filter((row) => isPreviousRow(row)).length,
);

const duplicateActualKeys = computed(() => {
  const counts = new Map<string, number>();

  for (const line of internalCostInvoiceLines.value) {
    if (line.invoiceItemSource && line.invoiceItemSource !== 'actual') {
      continue;
    }

    const key = getInternalKey(line);
    if (!key) {
      continue;
    }

    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([key]) => key),
  );
});

const headerTitle = computed(
  () => `${isCreate.value ? t('labels.new') : t('labels.edit')} ${t('text.invoiceLineItem')}`,
);

// 🎯 Available suggestions for LineForm modal (all possible invoices to match against)
// const availableSuggestionsForLineForm = computed(() => {
//   const suggestions: OperatingCostInvoiceSimple[] = [];

//   // Add AI suggestions
//   // (In real implementation, these would come from props, but for now just use importSourceInvoices)

//   // Add latest invoice
//   if (props.latestInvoiceId) {
//     suggestions.push({
//       id: props.latestInvoiceId,
//       lineItems: props.latestInvoiceLineItems ?? [],
//     } as any);
//   }

//   // Add import source invoices (if available)
//   if (props.importSourceInvoices && props.importSourceInvoices.length > 0) {
//     suggestions.push(...props.importSourceInvoices);
//   }

//   return suggestions;
// });

// Helper: Stable identity key for matching/deduplication.
// Preferred behavior: exact match is sfmPositionId + nameOnStatement.
// For AI suggestions without an SFM mapping yet, fall back to nameOnStatement so
// those rows still render in step 2 instead of being dropped entirely.
const getInternalKey = (line: OperatingCostInvoiceLineItemSimple): string => {
  const sfmPositionId = normalizeMatchValue(line.sfmPositionId);
  const nameOnStatement = normalizeMatchValue(line.nameOnStatement);
  if (sfmPositionId && nameOnStatement) {
    return `${sfmPositionId}::${nameOnStatement}`;
  }

  if (nameOnStatement) {
    return `name::${nameOnStatement}`;
  }

  const sfmPositionName = normalizeMatchValue(line.sfmPositionName);
  if (sfmPositionName) {
    return `sfm-name::${sfmPositionName}`;
  }

  if (sfmPositionId) {
    return `sfm-id::${sfmPositionId}`;
  }

  if (normalizeMatchValue(line.id)) {
    return `id::${normalizeMatchValue(line.id)}`;
  }

  return '';
};

// are there any lines from previous invoice not yet imported?
const hasImportableLines = computed(() => {
  const previousLines = props.latestInvoiceLineItems ?? [];
  if (!previousLines.length) return false;

  // all keys of actual invoice (actual + ggf. previous)
  const currentKeys = new Set(
    internalCostInvoiceLines.value.map((line) => getInternalKey(line)).filter((k) => k.length > 0),
  );

  // is there at least one last year`s item with a new key?
  return previousLines.some((prev) => {
    const key = getInternalKey(prev);
    return key.length > 0 && !currentKeys.has(key);
  });
});

const hasPreviousInvoice = computed(() => {
  const hasId = !!props.latestInvoiceId;
  const hasLineItems = (props.latestInvoiceLineItems?.length ?? 0) > 0;
  return hasId && hasLineItems;
});

const showImportButton = computed(
  () => editMode.value && hasPreviousInvoice.value && hasImportableLines.value,
);

function mergeImportedLines(
  dtoLines: OperatingCostInvoiceLineItemSimple[],
  sourceType?: InvoiceItemSuggestionSource,
) {
  if (!dtoLines.length) {
    console.warn('[Lines] No line items to import');
    return;
  }

  console.log(
    `[mergeImportedLines] Merging ${dtoLines.length} lines with sourceType="${sourceType}"`,
  );

  // Maps existing lines by their internal key
  const existingByKey = new Map<string, OperatingCostInvoiceLineItemSimple>(
    internalCostInvoiceLines.value.map((line) => [getInternalKey(line), line]),
  );

  // Mark existing lines as 'actual'
  const merged: OperatingCostInvoiceLineItemWithSource[] = internalCostInvoiceLines.value.map(
    (line) => ({
      ...line,
      invoiceItemSource: line.invoiceItemSource ?? 'actual',
    }),
  );

  // Only add new keys with source type (ai | latest | other, or fallback to 'previous')
  for (const prev of dtoLines) {
    const key = getInternalKey(prev);
    if (!key) continue;
    if (existingByKey.has(key)) continue; // new invoice wins

    const newId = v4();
    merged.push({
      ...prev,
      id: newId,
      version: 0,
      invoiceItemSource: sourceType ?? 'previous', // 🎯 Use sourceType if provided, fallback to 'previous'
    } as any);

    // 🎯 Track source type in map when we have one
    if (sourceType && (sourceType === 'ai' || sourceType === 'latest' || sourceType === 'other')) {
      lineItemSourceTypeMap.value.set(newId, sourceType);
      console.log(`[mergeImportedLines] Tracked source for line ${newId}: ${sourceType}`);
    }
  }

  internalCostInvoiceLines.value = merged;
}

// Import from the invoice selected in CascadeSelect
const onImportFromSelected = () => {
  const inv = selectedImportInvoice.value;
  if (!inv) return;

  const lines = inv.lineItems ?? [];
  mergeImportedLines(lines);
};

/** Called when creating a line on a not-yet-persisted invoice */
const onAddCostInvoiceLineToCreate = (item: CreateOperatingCostInvoiceLineItemV1Dto) => {
  internalCostInvoiceLines.value.push({
    ...(item as any),
    id: v4(),
    version: 0,
    invoiceItemSource: 'actual', // lines created here are already "actual"
  });

  // Emit success event to parent
  emit('lineItemCreated', { ok: true });
};

const onAddCostInvoiceLineToUpdate = (item: OperatingCostInvoiceLineItemSimple) => {
  const lines = internalCostInvoiceLines.value;
  const index = lines.findIndex((x) => x.id === item.id);

  if (index !== -1) {
    // Ensure TypeScript knows we preserve required properties from the existing entry
    const existing = internalCostInvoiceLines.value[
      index
    ] as OperatingCostInvoiceLineItemWithSource;
    internalCostInvoiceLines.value[index] = {
      ...existing,
      ...item,
      invoiceItemSource: existing.invoiceItemSource ?? 'actual',
    };
  } else {
    lines.push({ ...item, invoiceItemSource: 'actual' });
  }

  // Emit success event to parent
  emit('lineItemCreated', { ok: true });
};

function onDeleteActualSelected(confirmDeleteSelected: () => void) {
  const actualRows = selectedRows.value.filter((row) => isActualRow(row));

  if (!actualRows.length) {
    return;
  }

  // Restrict selection to deletable (Actual) rows only
  // DataTable uses this selection for its internal delete logic
  selectedRows.value = actualRows;

  // Now trigger the DataTable's confirm + delete flow
  confirmDeleteSelected();
}

// Helper: Map a table row back to the underlying line item
function findLineItemForRow(row: any): OperatingCostInvoiceLineItemSimple | undefined {
  const id = row?.id?.meta?.('rawvalue') ?? row?.id;
  if (!id) return undefined;
  return internalCostInvoiceLines.value.find((x) => x.id === id);
}

// Helper: Check source flags
function isActualRow(row: any): boolean {
  const item = findLineItemForRow(row);
  // Treat missing flag as "Actual" (current)
  return !item?.invoiceItemSource || item.invoiceItemSource === 'actual';
}

function buildInitialHints() {
  // Get values WITH their IMPROVED source types (ai | latest | other)
  const nameOnStatementWithSources = collectUniqueValuesFor('nameOnStatement');
  const nameOnStatementValues = nameOnStatementWithSources.map((s) => s.value) as string[];
  const nameOnStatementSources = nameOnStatementWithSources.map((s) => s.source);

  // areaDesignation should suggest existing values from both visible rows and
  // the unfiltered suggestion pool while still allowing free text input.
  const areaDesignationValues = Array.from(
    new Set(
      [
        ...internalCostInvoiceLines.value.map((line) => line.areaDesignation),
        ...getUnfilteredSuggestionPool().map((line) => line.areaDesignation),
      ]
        .map((value) => String(value ?? '').trim())
        .filter((value) => value.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));

  console.log('[buildInitialHints] nameOnStatementValues:', nameOnStatementValues);
  console.log('[buildInitialHints] nameOnStatementSources:', nameOnStatementSources);

  // Reset suggestions, but preserve existing initialData
  const preservedInitialData: Record<string, any> = { ...(lineInitialData.value ?? {}) };
  lineSuggestions.value = {};
  lineSuggestionsSources.value = {};

  const applyString = (
    values: string[],
    sources: InvoiceItemSuggestionSource[],
    key: 'nameOnStatement' | 'sfmPositionName',
  ) => {
    if (values.length === 1) {
      // 🎯 ONLY set if not already in initialData (don't overwrite)
      if (!preservedInitialData[key]) {
        preservedInitialData[key] = values[0];
        console.log(`  ✓ Set ${key} initialData = "${values[0]}" (source: ${sources[0]})`);
      } else {
        console.log(
          `  ℹ️ Skipped ${key} - already in initialData = "${preservedInitialData[key]}"`,
        );
      }
    } else if (values.length > 1) {
      lineSuggestions.value[key] = values;
      (lineSuggestionsSources.value as any)[key] = sources;
      console.log(
        `  ✓ Set ${key} suggestions with ${values.length} options and sources:`,
        values,
        sources,
      );
    } else {
      console.log(`  ✗ No values found for ${key}`);
    }
  };

  applyString(nameOnStatementValues, nameOnStatementSources, 'nameOnStatement');

  if (areaDesignationValues.length > 0) {
    lineSuggestions.value.areaDesignation = areaDesignationValues;
  }

  // Update lineInitialData with preserved values
  lineInitialData.value = preservedInitialData;

  console.log('[buildInitialHints] Final suggestions:', lineSuggestions.value);
  console.log('[buildInitialHints] Final sources:', lineSuggestionsSources.value);
}

/** Clear all suggestions (non-actual lines) from the table */
function clearSuggestions() {
  console.log('[clearSuggestions] Removing all suggestion lines...');
  internalCostInvoiceLines.value = internalCostInvoiceLines.value.filter(
    (line) => line.invoiceItemSource === 'actual' || !line.invoiceItemSource,
  );
  console.log(
    `[clearSuggestions] Cleared. Remaining lines: ${internalCostInvoiceLines.value.length}`,
  );
}

function collectUniqueValuesFor(key: keyof OperatingCostInvoiceLineItemSimple): Array<{
  value: any;
  source: InvoiceItemSuggestionSource;
}> {
  const allLines = internalCostInvoiceLines.value;
  console.log(`[collectUniqueValuesFor] Key: "${String(key)}" | Total lines: ${allLines.length}`);

  // 🎯 Include BOTH 'actual' and 'previous' (KI) lines, map to source type
  const withSources = allLines
    .filter((line) => line.invoiceItemSource === 'actual' || line.invoiceItemSource === 'previous')
    .map((line) => {
      // Determine source based on invoiceItemSource and optional suggestionSourceType
      let source: InvoiceItemSuggestionSource = 'other';
      if (line.invoiceItemSource === 'actual') {
        source = 'other'; // Current lines
      } else if (line.invoiceItemSource === 'previous') {
        // For 'previous' lines, check the suggestionSourceType
        const typeHint = (line as any).suggestionSourceType;
        if (typeHint === 'ai') {
          source = 'ai';
        } else if (typeHint === 'latest') {
          source = 'latest';
        } else {
          source = 'other'; // Default for unknown previous
        }
      }

      return {
        value: line[key],
        source,
      };
    })
    .filter((item) => item.value !== undefined && item.value !== null);

  console.log(
    `[collectUniqueValuesFor] Collected ${withSources.length} items:`,
    withSources.map((s) => ({ value: s.value, source: s.source })),
  );

  // Remove duplicate VALUES, keep first occurrence
  const seen = new Map<string, { value: any; source: InvoiceItemSuggestionSource }>();
  withSources.forEach((item) => {
    const key = String(item.value);
    if (!seen.has(key)) {
      seen.set(key, item);
    }
  });

  const unique = Array.from(seen.values());
  console.log(`[collectUniqueValuesFor] Final unique: ${unique.length}`, unique);

  return unique;
}

function isPreviousRow(row: any): boolean {
  const item = findLineItemForRow(row);
  return isSuggestion(item?.invoiceItemSource);
}

function isDuplicateActualLine(line?: OperatingCostInvoiceLineItemSimple): boolean {
  if (!line) {
    return false;
  }

  if (line.invoiceItemSource && line.invoiceItemSource !== 'actual') {
    return false;
  }

  const key = getInternalKey(line);
  return !!key && duplicateActualKeys.value.has(key);
}

function isDuplicateActualRow(row: any): boolean {
  return isDuplicateActualLine(findLineItemForRow(row));
}

function getRenderedCellValue(row: any, field: string): string {
  const cell = row?.[field];

  if (cell?.meta) {
    return cell.meta('renderedValue') ?? cell.meta('rawvalue') ?? cell.meta('rawValue') ?? '';
  }

  return String(cell ?? '');
}

function buildLineItemsContextMenuItems({
  selectedRows,
  confirmDeleteSelected,
}: {
  selectedRows: any[];
  confirmDeleteSelected: () => void;
}): any[] {
  if (!selectedRows?.length) {
    return [];
  }

  const selectedSuggestionRows = selectedRows.filter((row) => isPreviousRow(row));
  const selectedActualRows = selectedRows.filter((row) => isActualRow(row));

  const row = selectedRows[0];
  const line = findLineItemForRow(row);
  if (!line) {
    return [];
  }

  const canEdit = !props.disableRowModal;

  const items: any[] = [];

  if (selectedSuggestionRows.length > 0) {
    items.push({
      label: t('labels.apply'),
      icon: 'pi pi-check',
      command: () => {
        // Use the same logic as the toolbar "apply" button.
        void onApplyPreviousSelected();
      },
    });
  }

  if (canEdit && selectedRows.length === 1) {
    items.push({
      label: props.readOnlyModal ? t('labels.view') : t('labels.edit'),
      icon: props.readOnlyModal ? 'pi pi-eye' : 'pi pi-pencil',
      command: () => {
        onRowClick({
          data: row,
          originalEvent: {
            altKey: false,
            ctrlKey: false,
            metaKey: false,
            shiftKey: false,
            button: 0,
          } as MouseEvent,
        });
      },
    });
  }

  if (selectedActualRows.length > 0) {
    items.push({
      label: t('labels.delete'),
      icon: 'pi pi-trash',
      command: () => {
        // Use the same logic as the toolbar "delete selected actual" button.
        onDeleteActualSelected(confirmDeleteSelected);
      },
    });
  }

  return items;
}

async function onApplyPreviousSelected() {
  // Filter only on selected previous lines
  const previousRows = selectedRows.value.filter((row) => isPreviousRow(row));

  if (!previousRows.length) {
    return;
  }

  // Persist to backend (creates real Actual lines on current invoice)
  await applySelectedLines(previousRows);

  // Optional: Clear selection
  selectedRows.value = [];
}

/**
 * 🎯 Restore suggestions after fetchData() reload
 * If a template is still selected (props.templateSource), re-apply its suggestions
 */
function restoreTemplateAfterReload() {
  console.log('[restoreTemplateAfterReload] Checking if template needs restore...', {
    templateSource: props.templateSource,
    selectedImportSourceInvoiceId: props.selectedImportSourceInvoiceId,
    hasImportInvoices: (props.importSourceInvoices?.length ?? 0) > 0,
    hasLatestInvoice: !!props.latestInvoiceId,
    lastLoadedSource: lastLoadedImportSource.value,
  });

  // 1️⃣ If AI template was selected, re-merge from the SELECTED importSourceInvoice only
  if (
    props.templateSource === 'ai' &&
    props.importSourceInvoices &&
    props.importSourceInvoices.length > 0
  ) {
    console.log('[restoreTemplateAfterReload] ✅ Restoring AI suggestions...');

    // Try to use previously loaded source if available
    let sourceToRestore = lastLoadedImportSource.value;

    // Otherwise, use the currently selected source
    if (!sourceToRestore && props.selectedImportSourceInvoiceId) {
      const selectedInvoice = props.importSourceInvoices.find(
        (inv) => inv.id === props.selectedImportSourceInvoiceId,
      );
      if (selectedInvoice?.lineItems && selectedInvoice.lineItems.length > 0) {
        sourceToRestore = {
          sourceId: selectedInvoice.id,
          sourceType: 'ai',
          lineItems: selectedInvoice.lineItems,
        };
      }
    }

    // If we have a source to restore, merge ONLY that one
    if (sourceToRestore) {
      console.log(
        `[restoreTemplateAfterReload] Merging only selected source: ${sourceToRestore.sourceId}`,
      );
      mergeImportedLines(sourceToRestore.lineItems, sourceToRestore.sourceType);
      lastLoadedImportSourceOriginal.value = {
        sourceId: sourceToRestore.sourceId,
        sourceType: sourceToRestore.sourceType,
        lineItems: sourceToRestore.lineItems,
      };
      return;
    }

    console.log('[restoreTemplateAfterReload] ⚠️ AI template set but no selected source found');
    return;
  }

  // 2️⃣ If LATEST template was selected, re-merge from latestInvoiceLineItems
  if (
    props.templateSource === 'latest' &&
    props.latestInvoiceLineItems &&
    props.latestInvoiceLineItems.length > 0
  ) {
    console.log(
      '[restoreTemplateAfterReload] ✅ Restoring LATEST suggestions from latestInvoiceLineItems',
    );

    mergeImportedLines(props.latestInvoiceLineItems, 'latest');
    // Save for potential refresh after delete
    lastLoadedImportSourceOriginal.value = {
      sourceId: props.latestInvoiceId ?? 'latest',
      sourceType: 'latest',
      lineItems: props.latestInvoiceLineItems,
    };
    return;
  }

  console.log('[restoreTemplateAfterReload] ℹ️ No template to restore or no data available');
}

const onModalClose = async (reopenForNextEntry: boolean = false) => {
  open.value = false;
  isCreate.value = false;

  // 🎯 ALWAYS call fetchData, even for new invoices
  if (typeof props.fetchData === 'function') {
    await props.fetchData();
  }

  // 🎯 Force DataTable re-render by incrementing key
  tableRenderKey.value++;

  if (reopenForNextEntry) {
    // Reset state for next entry
    selectedRow.value = undefined;
    selectedRows.value = [];
    lineInitialData.value = null;
    lineDisabledFields.value = {};

    // 🎯 RESTORE suggestions for active template after reload
    restoreTemplateAfterReload();

    // 🎯 Remove duplicate suggestions after modal close
    removeDuplicateSuggestionsAfterApply();

    // 🎯 Rebuild suggestions based on updated data
    buildInitialHints();

    // 🎯 Clear selection after save
    selectedRows.value = [];

    // Set up state for creating a new line
    isCreate.value = true;

    // Reopen the modal with fresh state
    open.value = true;
  }
};

function getUnfilteredSuggestionPool(): OperatingCostInvoiceLineItemSimple[] {
  const candidates: OperatingCostInvoiceLineItemSimple[] = [];

  // 1) Prefer the preserved original source used for template/suggestions.
  if (lastLoadedImportSourceOriginal.value?.lineItems?.length) {
    candidates.push(...lastLoadedImportSourceOriginal.value.lineItems);
  }

  // 2) Fallback to currently selected import source invoice.
  if (props.selectedImportSourceInvoiceId && props.importSourceInvoices?.length) {
    const selectedImportSource = props.importSourceInvoices.find(
      (inv) => inv.id === props.selectedImportSourceInvoiceId,
    );
    if (selectedImportSource?.lineItems?.length) {
      candidates.push(...selectedImportSource.lineItems);
    }
  }

  // 3) Fallback to latest invoice suggestions.
  if (props.latestInvoiceLineItems?.length) {
    candidates.push(...props.latestInvoiceLineItems);
  }

  // 4) Last fallback: currently visible suggestion rows.
  const visibleSuggestions = internalCostInvoiceLines.value.filter((line) =>
    isSuggestion(line.invoiceItemSource),
  );
  if (visibleSuggestions.length) {
    candidates.push(...visibleSuggestions);
  }

  // Deduplicate by business key to avoid conflicting duplicates from multiple pools.
  const seenKeys = new Set<string>();
  const uniqueCandidates: OperatingCostInvoiceLineItemSimple[] = [];

  for (const candidate of candidates) {
    const key = getInternalKey(candidate);
    if (!key || seenKeys.has(key)) continue;
    seenKeys.add(key);
    uniqueCandidates.push(candidate);
  }

  return uniqueCandidates;
}

/**
 * 🎯 Build dynamic placeholders based on context
 * - If opening a suggestion → use suggestion data as placeholders
 * - If opening an actual item → find matching draft/proposal and use its data as placeholders
 */
function buildDynamicPlaceholders(currentLine: OperatingCostInvoiceLineItemSimple) {
  // Reset to empty first
  linePlaceholders.value = {};

  // 1️⃣ If this is a SUGGESTION → use its data as placeholders
  if (isSuggestion(currentLine.invoiceItemSource)) {
    console.log('[buildDynamicPlaceholders] 💡 Suggestion detected - using its data:', {
      nameOnStatement: currentLine.nameOnStatement,
      customerArea: currentLine.customerArea,
      customerAmount: currentLine.customerAmount,
      totalArea: currentLine.totalArea,
      totalAmount: currentLine.totalAmount,
      isFlatRate: currentLine.isFlatRate,
      status: currentLine.status,
    });

    linePlaceholders.value = {
      areaDesignation: resolveAreaDesignationPlaceholder(currentLine.areaDesignation),
      nameOnStatement: currentLine.nameOnStatement || '',
      sfmPositionId: currentLine.sfmPositionId || '',
      customerArea: currentLine.customerArea?.toString() || '',
      customerAmount: currentLine.customerAmount?.toString() || '',
      totalArea: currentLine.totalArea?.toString() || '',
      totalAmount: currentLine.totalAmount?.toString() || '',
      isFlatRate: currentLine.isFlatRate ? 'true' : 'false',
      status: currentLine.status || '',
    };
    return;
  }

  // 2️⃣ If this is an ACTUAL ITEM → search for matching draft/proposal
  console.log(
    '[buildDynamicPlaceholders] 📋 Actual item detected - searching for matching draft...',
  );

  // Get all suggestions from the unfiltered source pool.
  const suggestions = getUnfilteredSuggestionPool();

  console.log(`[buildDynamicPlaceholders] Available suggestions: ${suggestions.length}`);

  // Strategy 1: Find exact match (same sfmPositionId AND same nameOnStatement)
  const exactMatch = suggestions.find(
    (sug) =>
      normalizeMatchValue(sug.sfmPositionId) === normalizeMatchValue(currentLine.sfmPositionId) &&
      normalizeMatchValue(sug.nameOnStatement) === normalizeMatchValue(currentLine.nameOnStatement),
  );

  if (exactMatch) {
    console.log('[buildDynamicPlaceholders] ✅ Found EXACT match (SFM ID + nameOnStatement):', {
      sfmPositionId: exactMatch.sfmPositionId,
      nameOnStatement: exactMatch.nameOnStatement,
    });

    // Exact match keys are already equal; show only other fields as suggestions.
    linePlaceholders.value = {
      areaDesignation: resolveAreaDesignationPlaceholder(exactMatch.areaDesignation),
      customerArea: exactMatch.customerArea?.toString() || '',
      customerAmount: exactMatch.customerAmount?.toString() || '',
      totalArea: exactMatch.totalArea?.toString() || '',
      totalAmount: exactMatch.totalAmount?.toString() || '',
      isFlatRate: exactMatch.isFlatRate ? 'true' : 'false',
      status: exactMatch.status || '',
    };
    return;
  }

  // If nameOnStatement is set on current line, do not fallback to SFM-only matching.
  // Otherwise we can incorrectly show placeholders from a different line that shares
  // only sfmPositionId (e.g. Abwasser vs Niederschlagswasser).
  if (normalizeMatchValue(currentLine.nameOnStatement)) {
    console.log(
      '[buildDynamicPlaceholders] ❌ No exact match for SFM ID + nameOnStatement; skipping SFM-only fallback',
    );
    linePlaceholders.value = {};
    return;
  }

  // Strategy 2: Find by SFM ID only
  const sfmMatches = suggestions.filter(
    (sug) =>
      normalizeMatchValue(sug.sfmPositionId) === normalizeMatchValue(currentLine.sfmPositionId),
  );

  if (sfmMatches.length === 1) {
    const sfmMatch = sfmMatches[0];
    console.log('[buildDynamicPlaceholders] ✅ Found SFM match (exactly 1):', {
      sfmPositionId: sfmMatch.sfmPositionId,
      nameOnStatement: sfmMatch.nameOnStatement,
    });

    linePlaceholders.value = {
      areaDesignation: resolveAreaDesignationPlaceholder(sfmMatch.areaDesignation),
      nameOnStatement: sfmMatch.nameOnStatement || '',
      sfmPositionId: sfmMatch.sfmPositionId || '',
      customerArea: sfmMatch.customerArea?.toString() || '',
      customerAmount: sfmMatch.customerAmount?.toString() || '',
      totalArea: sfmMatch.totalArea?.toString() || '',
      totalAmount: sfmMatch.totalAmount?.toString() || '',
      isFlatRate: sfmMatch.isFlatRate ? 'true' : 'false',
      status: sfmMatch.status || '',
    };
    return;
  }

  if (sfmMatches.length > 1) {
    console.log(
      `[buildDynamicPlaceholders] ⚠️ Found ${sfmMatches.length} SFM matches - too many, skipping`,
    );
  }

  // No match found
  console.log('[buildDynamicPlaceholders] ❌ No matching draft found - using empty placeholders');
  linePlaceholders.value = {};
}

const onRowClick = (rowEvent: LineRowClickEvent) => {
  if (!isPlainRowClick(rowEvent)) {
    return;
  }

  if (props.disableRowModal) {
    return;
  }

  const rowId = rowEvent.data.id._meta.rawvalue;
  const line = internalCostInvoiceLines.value.find((x) => x.id === rowId);

  if (!line) return;

  if (props.readOnlyModal) {
    isCreate.value = false;
    selectedRow.value = { ...line };
    lineInitialData.value = null;
    lineDisabledFields.value = {};
    buildInitialHints();
    buildDynamicPlaceholders(line);
    open.value = true;
    return;
  }

  // Extract the source type correctly (handle meta-enriched values)
  const sourceType = line.invoiceItemSource;
  const isPreviousType =
    sourceType === 'previous' ||
    sourceType === 'ai' ||
    sourceType === 'latest' ||
    sourceType === 'other';

  if (isPreviousType) {
    // Clicking on any suggestion (ai/latest/other/previous) → always create new mode
    // This works for both existing and new invoices
    isCreate.value = true;

    // Suggestion modal should show proposal values as placeholders, not as prefilled values.
    lineInitialData.value = null;
    lineDisabledFields.value = {};

    buildInitialHints();

    // No existing row to update
    selectedRow.value = undefined;

    // 🎯 Build dynamic placeholders from suggestion
    buildDynamicPlaceholders(line);
  } else {
    // Clicking on actual item → normal edit mode
    isCreate.value = false;
    selectedRow.value = { ...line };
    lineInitialData.value = null;
    lineDisabledFields.value = {};

    buildInitialHints();

    // 🎯 Build dynamic placeholders (search for matching draft)
    buildDynamicPlaceholders(line);
  }

  // 🎯 Debug: Log what's being sent to modal
  console.log('[Lines:onRowClick] Opening modal with:', {
    isCreate: isCreate.value,
    selectedRow: selectedRow.value,
    lineInitialData: lineInitialData.value,
    linePlaceholders: linePlaceholders.value,
    templateSource: props.templateSource,
  });

  open.value = true;
};

const handleCreate = () => {
  isCreate.value = true;
  selectedRow.value = undefined;
  lineInitialData.value = null;
  lineDisabledFields.value = {};

  buildInitialHints();

  // 🎯 For new item creation, reset placeholders to defaults
  // (user isn't editing an existing item, so no draft to search for)
  linePlaceholders.value = {};

  // 🎯 Debug: Log what's being sent to modal
  console.log('[Lines:handleCreate] Opening new item modal with:', {
    linePlaceholders: linePlaceholders.value,
    templateSource: props.templateSource,
  });

  open.value = true;
};

/**
 * 🎯 Handle single row delete (for actual invoice items)
 * Called from custom row-action button for items with invoiceItemSource: 'actual'
 */
const handleDeleteSingle = async (id: string) => {
  await handleDelete([id]);
};

/**
 * 🎯 Handle suggestion row action (open form to add to invoice)
 * Called from custom row-action button for suggestions (ai/latest/other/previous)
 * Pre-fills the form with the suggestion data
 */
const handleCreateFromRow = (row: any) => {
  // Extract the raw line item data (handle both meta-enriched and plain rows)
  const lineItem = findLineItemForRow(row);
  if (!lineItem) {
    console.warn('[Lines] Could not find line item for row:', row);
    return;
  }

  // Open form in create mode with suggestion pre-filled
  isCreate.value = true;

  // Keep suggestion values as placeholders (not as prefilled form values)
  // so user edits are preserved and not overridden by initialData.
  lineInitialData.value = null;
  lineDisabledFields.value = {};

  buildInitialHints();
  buildDynamicPlaceholders(lineItem);

  // No selected row to update
  selectedRow.value = undefined;

  open.value = true;
};

/**
 * After deleting actual lines in a new invoice, refresh suggestions from the last loaded source.
 * This makes hidden suggestions visible again if their actual entry was deleted.
 * Simply re-merges the source - duplicates are automatically filtered out.
 */
function refreshSuggestionsAfterDelete() {
  if (!lastLoadedImportSourceOriginal.value) {
    console.log(
      '[refreshSuggestionsAfterDelete] No saved original import source, nothing to refresh',
    );
    return;
  }

  const { sourceId, sourceType, lineItems } = lastLoadedImportSourceOriginal.value;

  console.log(
    `[refreshSuggestionsAfterDelete] Refreshing suggestions from source: ${sourceId} (${sourceType}) with ${lineItems.length} items`,
  );

  // 🎯 Simply re-merge: Will add back suggestions whose actual entries were deleted
  // (because mergeImportedLines checks `if (existingByKey.has(key)) continue`)
  // Now that actual is gone, the suggestion will be added back
  mergeImportedLines(lineItems, sourceType);

  console.log('[refreshSuggestionsAfterDelete] Refresh complete');
}

const handleDelete = async (selectedIdsToDelete: string[]) => {
  internalCostInvoiceLines.value = internalCostInvoiceLines.value.filter(
    (x) => !selectedIdsToDelete.includes(x.id),
  );

  if (!props.isNew) {
    try {
      const invoiceID = props.invoiceId ?? subComponentID.value;
      await deleteOperatingCostInvoiceLines(String(invoiceID), selectedIdsToDelete);
    } finally {
      await props.fetchData();
      // 🎯 NEW: For new invoices, refresh suggestions from the current source
      // This will re-show suggestions whose actual entries were deleted
      refreshSuggestionsAfterDelete();
    }
  }

  // 🎯 Clear selection after delete
  selectedRows.value = [];
};

const getInvoiceLineRowClass = (row: any): string => {
  const item = findLineItemForRow(row);
  const classes: string[] = [];

  if (props.showSuggestionRowStyle && isSuggestion(item?.invoiceItemSource)) {
    // Mark all suggestion rows (ai | latest | other | previous) with kursiv + grau styling.
    classes.push('invoice-line-suggestion');
  }

  if (isDuplicateActualLine(item)) {
    classes.push('invoice-line-duplicate');
  }

  return classes.join(' ');
};

/**
 * Import + merge:
 * - existing lines are always kept (invoiceItemSource = 'actual')
 * - previous-year lines from latestInvoiceLineItems are added if their sfmPositionName is new
 * - imported lines get invoiceItemSource = 'previous'
 */
const importFromLatest = () => {
  const dtoLines = props.latestInvoiceLineItems ?? [];
  if (!dtoLines.length) {
    console.warn('No line items in latest invoice');
    return;
  }

  mergeImportedLines(dtoLines);
};

const importFromInvoice = async (invoiceId: string) => {
  try {
    const dto = await fetchOperatingCostInvoice(invoiceId);
    const dtoLines =
      convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList(
        dto.lineItems ?? [],
      );

    mergeImportedLines(dtoLines);
  } catch (err) {
    console.error('[Lines] Failed to import from invoice', invoiceId, err);
  }
};

defineExpose({
  importFromInvoice,
});

/**
 * Persist selected previous-year lines to current invoice
 * and reload from backend afterwards.
 */
/**
 * After applying suggestions and reloading data, remove suggestion lines that now exist as actual lines.
 * Matching is done by exact key: sfmPositionId + nameOnStatement.
 */
function removeDuplicateSuggestionsAfterApply() {
  console.log('[removeDuplicateSuggestionsAfterApply] Checking for duplicate suggestions...');

  // Get all exact keys from actual lines
  const actualKeys = new Set(
    internalCostInvoiceLines.value
      .filter((line) => line.invoiceItemSource === 'actual' || !line.invoiceItemSource)
      .map((line) => getInternalKey(line))
      .filter((key) => !!key),
  );

  console.log(
    `[removeDuplicateSuggestionsAfterApply] Actual keys: ${Array.from(actualKeys).join(', ')}`,
  );

  // Count suggestions before cleanup
  const suggestionsBeforeCount = internalCostInvoiceLines.value.filter((line) =>
    isSuggestion(line.invoiceItemSource),
  ).length;

  // Remove suggestions whose exact key now exists in actual lines
  internalCostInvoiceLines.value = internalCostInvoiceLines.value.filter((line) => {
    // Keep all actual lines
    if (line.invoiceItemSource === 'actual' || !line.invoiceItemSource) {
      return true;
    }

    const suggestionKey = getInternalKey(line);
    const isDuplicate = !!suggestionKey && actualKeys.has(suggestionKey);
    if (isDuplicate) {
      console.log(
        `[removeDuplicateSuggestionsAfterApply] Removed duplicate suggestion: key="${suggestionKey}"`,
      );
    }
    return !isDuplicate;
  });

  const suggestionsAfterCount = internalCostInvoiceLines.value.filter((line) =>
    isSuggestion(line.invoiceItemSource),
  ).length;

  console.log(
    `[removeDuplicateSuggestionsAfterApply] Cleaned up: ${suggestionsBeforeCount} → ${suggestionsAfterCount} suggestions`,
  );
}

const applySelectedLines = async (selectedRows: any[]) => {
  const invoiceID = props.invoiceId ?? subComponentID.value;
  if (!invoiceID) {
    console.warn('[Lines] Cannot apply selection without invoiceId');
    return;
  }

  const selectedIds = selectedRows
    ?.map((row) => row.id?.meta?.('rawvalue') ?? row.id)
    .filter(Boolean) as string[];

  // 🎯 Filter all suggestion lines (ai | latest | other | previous) - anything that's NOT 'actual'
  const linesToPersist = internalCostInvoiceLines.value.filter(
    (line) => selectedIds.includes(line.id) && line.invoiceItemSource !== 'actual',
  );

  if (!linesToPersist.length) {
    console.log('[Lines] No suggestion lines selected to apply');
    return;
  }

  for (const line of linesToPersist) {
    // Strip invoiceItemSource before converting to DTO
    const { invoiceItemSource: _src, ...simple } = line;

    const dto: CreateOperatingCostInvoiceLineItemV1Dto =
      convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(simple);

    await createOperatingCostInvoiceLine(String(invoiceID), dto);
  }

  // After persisting, reload full invoice → new lines come back as 'actual'
  await props.fetchData();

  // 🎯 NEW: Remove suggestion duplicates that now exist as actual lines
  removeDuplicateSuggestionsAfterApply();

  // Emit success event to parent
  emit('lineItemCreated', { ok: true });
};

// Keep parent in sync for "new invoice" mode only
const stripSource = (
  lines: OperatingCostInvoiceLineItemWithSource[],
): OperatingCostInvoiceLineItemSimple[] =>
  lines.map(({ invoiceItemSource: _invoiceItemSource, ...rest }) => rest);

watch(
  internalCostInvoiceLines,
  (newValues) => {
    if (props.isNew) {
      props.onUpdateLineItemsOnCreate(stripSource(newValues));
    }
  },
  { immediate: true },
);

// Initialize / refresh internal lines from props (always as 'actual')
watch(
  () => props.existingOperatingCostInvoiceLines,
  (newVal) => {
    // 🎯 CRITICAL FIX: Merge instead of replace
    // This preserves invoiceItemSource from imported lines

    if (!newVal || newVal.length === 0) {
      internalCostInvoiceLines.value = [];
      return;
    }

    const incomingIds = new Set(newVal.map((line) => line.id));

    // Keep existing imported lines (ai/latest/other/previous sources)
    const importedLines = internalCostInvoiceLines.value.filter(
      (line) => !incomingIds.has(line.id) && line.invoiceItemSource !== 'actual',
    );

    // Mark incoming lines as 'actual'
    const actualLines = newVal.map((line) => ({
      ...line,
      invoiceItemSource: props.forceInvoiceItemSource ?? ('actual' as const),
    }));

    // Merge: actual lines from backend + imported suggestion lines
    internalCostInvoiceLines.value = [...actualLines, ...importedLines];

    // Keep table clean: hide suggestions already represented by actual rows.
    removeDuplicateSuggestionsAfterApply();
  },
  { immediate: true, deep: true },
);

// Auto-load line items from selected import source invoice when ID changes
watch(
  () => props.selectedImportSourceInvoiceId,
  (selectedId) => {
    // If no source selected, clear all suggestions
    if (!selectedId) {
      clearSuggestions();
      lastLoadedImportSource.value = null;
      lastLoadedImportSourceOriginal.value = null;
      console.log(
        '[Watch selectedImportSourceInvoiceId] No source selected, cleared all suggestions',
      );
      return;
    }

    if (!props.importSourceInvoices || props.importSourceInvoices.length === 0) {
      return;
    }

    // Validate: Is this ID actually from importSourceInvoices (not from AI or other sources)?
    const selectedInvoice = props.importSourceInvoices.find((inv) => inv.id === selectedId);

    if (!selectedInvoice) {
      return;
    }

    if (selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0) {
      // 🎯 CRITICAL: Clear old suggestions (non-actual lines) before adding new ones from the switched source
      clearSuggestions();

      // Determine source type from invoiceSourceMap
      const sourceType = props.invoiceSourceMap?.[selectedId] ?? 'other';
      console.log(
        `[Watch selectedImportSourceInvoiceId] Switched to source: ${selectedId} (type: ${sourceType}), cleared old suggestions and merging new ones`,
      );

      // 🎯 NEW: Store this source for rebuild-after-delete
      // Save BOTH current and original (deep copy) to preserve original data
      lastLoadedImportSource.value = {
        sourceId: selectedId,
        sourceType: sourceType,
        lineItems: selectedInvoice.lineItems,
      };

      // 🎯 Save ORIGINAL (deep copy) for rebuilding after delete
      lastLoadedImportSourceOriginal.value = {
        sourceId: selectedId,
        sourceType: sourceType,
        lineItems: JSON.parse(JSON.stringify(selectedInvoice.lineItems)), // Deep copy
      };

      mergeImportedLines(selectedInvoice.lineItems, sourceType);
    }
  },
);

// 🎯 Watch for template source changes
// When the template (AI/Latest) is selected/changed, automatically restore its suggestions
watch(
  () => props.templateSource,
  (newTemplateSource) => {
    console.log('[Watch templateSource] Template changed to:', newTemplateSource);

    if (!newTemplateSource) {
      // Template was deselected - clear suggestions if needed
      console.log(
        '[Watch templateSource] Template deselected, suggestions will be cleared on next action',
      );
      return;
    }

    // Template is set - restore its suggestions
    console.log('[Watch templateSource] Template selected, restoring suggestions...');
    restoreTemplateAfterReload();
  },
);
</script>

<!-- frontend-zelos-next/src/views/operatingcostinvoices/components/operationcostinvoicelines/OperatingCostInvoiceLines.vue -->
<style lang="css">
@config "../../../../../tailwind.config.js";
@reference "tailwindcss";
/* Style for all suggestion rows (ai | latest | other | previous) */
.invoice-line-suggestion .table-cell {
  font-style: italic;
  @apply text-gray-500;
}

/* Style for duplicate actual rows (same sfmPositionId + nameOnStatement) */
.invoice-line-duplicate .table-cell {
  background-color: #fff7d6;
}

.duplicate-warning-icon {
  color: #b45309;
  font-size: 0.875rem;
}
</style>
